import { StepStatus } from '@/app/components/Modal/StepIcon';

export enum DepositSubStepStatus {
  Ready = 'ready',
  Locked = 'locked',
  Success = 'success',
  AwaitingWallet = 'awaiting_wallet',
  Loading = 'loading',
  Error = 'error',
}

export enum DepositStageStatus {
  Current = 'current',
  Completed = 'completed',
  Pending = 'pending',
}

export enum DepositStepperAlertVariant {
  Info = 'info',
  Error = 'error',
}

export enum DepositStepperFlow {
  SwapBatch = 'swap-batch',
  SwapNoBatch = 'swap-no-batch',
}

export type DepositSubStepView = {
  id: string;
  title: string;
  status: DepositSubStepStatus;
  statusLabel: string;
};

export type DepositStageView = {
  id: string;
  title: string;
  status: DepositStageStatus;
  statusLabel: string;
  subSteps: DepositSubStepView[];
};

export type DepositStepperCopyCtx = { trancheLabel: string };

type CopyText = string | ((ctx: DepositStepperCopyCtx) => string);

type StepCopy = {
  title: CopyText;
  primary: {
    [DepositSubStepStatus.Loading]: string;
    [DepositSubStepStatus.Error]: string;
  };
  alert: {
    [DepositSubStepStatus.Ready]: CopyText;
    [DepositSubStepStatus.Loading]: CopyText;
    [DepositSubStepStatus.Error]: CopyText;
    [DepositSubStepStatus.AwaitingWallet]?: CopyText;
  };
  statusLabel?: Partial<Record<DepositSubStepStatus, string>>;
};

type StageCopy = {
  id: string;
  title: CopyText;
  completedLabel: string;
  stepIds: string[];
  uiStepId?: string;
};

export const STAGE_STATUS_LABEL: Record<
  Exclude<DepositStageStatus, DepositStageStatus.Completed>,
  string
> = {
  [DepositStageStatus.Current]: 'Current stage',
  [DepositStageStatus.Pending]: 'Not started',
};

export const SUB_STEP_STATUS_LABEL: Record<DepositSubStepStatus, string> = {
  [DepositSubStepStatus.Success]: 'Confirmed',
  [DepositSubStepStatus.AwaitingWallet]: 'Ready · wallet request required',
  [DepositSubStepStatus.Loading]: 'Submitted · confirming on network',
  [DepositSubStepStatus.Locked]: 'Available after approval',
  [DepositSubStepStatus.Error]: 'Signature declined · not submitted',
  [DepositSubStepStatus.Ready]: 'Ready · wallet request required',
};

export const SUB_STEP_APP_ERROR_STATUS_LABEL = 'Failed · not submitted';

export const subStepStatusLabel = (
  status: DepositSubStepStatus,
  options?: { isUserRejected?: boolean },
): string => {
  if (status === DepositSubStepStatus.Error && options?.isUserRejected === false) {
    return SUB_STEP_APP_ERROR_STATUS_LABEL;
  }
  return SUB_STEP_STATUS_LABEL[status];
};

export const STAGE_TITLE_SEPARATOR = ' → ';

export const FLOW_COPY = {
  completePrimary: 'View position',
  continuePrimary: 'Continue',
  fallbackAlert: 'Follow the wallet prompts to complete each stage.',
  completeAlert: (ctx: DepositStepperCopyCtx) =>
    `You've successfully deposited USDC into ${ctx.trancheLabel}.`,
} as const;

export const STEP_STATUS_TO_SUB: Partial<Record<StepStatus, DepositSubStepStatus>> = {
  [StepStatus.Success]: DepositSubStepStatus.Success,
  [StepStatus.AwaitingWallet]: DepositSubStepStatus.AwaitingWallet,
  [StepStatus.Loading]: DepositSubStepStatus.Loading,
  [StepStatus.Error]: DepositSubStepStatus.Error,
};

export const STEP_COPY: Record<string, StepCopy> = {
  'approve-swap': {
    title: 'Approve USDC',
    primary: {
      [DepositSubStepStatus.Loading]: 'Confirming approval',
      [DepositSubStepStatus.Error]: 'Retry Approve USDC',
    },
    alert: {
      [DepositSubStepStatus.Ready]:
        'Approve USDC to allow the swap contract to spend it. Review the spending limit in your wallet.',
      [DepositSubStepStatus.Loading]: 'USDC approval was submitted. Waiting for network confirmation.',
      [DepositSubStepStatus.Error]:
        'You declined the approval signature. Nothing was submitted; you can try again.',
    },
  },
  'swap-usdc': {
    title: 'Swap to wYLDS',
    primary: {
      [DepositSubStepStatus.Loading]: 'Confirming swap',
      [DepositSubStepStatus.Error]: 'Retry swap to wYLDS',
    },
    statusLabel: {
      [DepositSubStepStatus.AwaitingWallet]: 'Sign Permit2 · then confirm swap',
      [DepositSubStepStatus.Ready]: 'Ready · Permit2 signature + swap tx',
    },
    alert: {
      [DepositSubStepStatus.Ready]:
        'USDC approval is confirmed. Next your wallet will ask for a Permit2 signature, then a separate swap transaction.',
      [DepositSubStepStatus.AwaitingWallet]:
        'Check your wallet: first sign the Permit2 message (not a transaction), then confirm the USDC → wYLDS swap.',
      [DepositSubStepStatus.Loading]:
        'Swap was submitted. Waiting for network confirmation.',
      [DepositSubStepStatus.Error]:
        'You declined the Permit2 signature or the swap. Nothing was swapped; you can try again.',
    },
  },
  'approve-wylds': {
    title: 'Approve wYLDS',
    primary: {
      [DepositSubStepStatus.Loading]: 'Confirming approval',
      [DepositSubStepStatus.Error]: 'Retry Approve wYLDS',
    },
    alert: {
      [DepositSubStepStatus.Ready]:
        'Approve wYLDS spending to mint mzUSD. Review the spending limit in your wallet.',
      [DepositSubStepStatus.Loading]: 'wYLDS approval was submitted. Waiting for network confirmation.',
      [DepositSubStepStatus.Error]:
        'You declined the approval signature. Nothing was submitted; you can try again.',
    },
  },
  'mint-mzusd': {
    title: 'Mint mzUSD',
    primary: {
      [DepositSubStepStatus.Loading]: 'Confirming mint',
      [DepositSubStepStatus.Error]: 'Retry mint mzUSD',
    },
    alert: {
      [DepositSubStepStatus.Ready]: 'wYLDS allowance is confirmed. You can now mint mzUSD.',
      [DepositSubStepStatus.Loading]:
        'Minting of mzUSD was submitted. Waiting for network confirmation.',
      [DepositSubStepStatus.Error]:
        'You declined the mint signature. No mint was submitted. Your wYLDS allowance is unchanged; you can try again.',
    },
  },
  'approve-mzusd': {
    title: 'Approve mzUSD',
    primary: {
      [DepositSubStepStatus.Loading]: 'Confirming approval',
      [DepositSubStepStatus.Error]: 'Retry Approve mzUSD',
    },
    alert: {
      [DepositSubStepStatus.Ready]: 'Allow the Vault to spend mzUSD.',
      [DepositSubStepStatus.Loading]: 'mzUSD approval was submitted. Waiting for network confirmation.',
      [DepositSubStepStatus.Error]:
        'You declined the approval signature. Nothing was submitted; you can try again.',
    },
  },
  'deposit-tranche': {
    title: (ctx) => `Deposit to ${ctx.trancheLabel}`,
    primary: {
      [DepositSubStepStatus.Loading]: 'Confirming deposit',
      [DepositSubStepStatus.Error]: 'Retry deposit',
    },
    alert: {
      [DepositSubStepStatus.Ready]: (ctx) =>
        `mzUSD approval is confirmed. You can now deposit into ${ctx.trancheLabel}.`,
      [DepositSubStepStatus.Loading]: (ctx) =>
        `Your mzUSD deposit to ${ctx.trancheLabel} was submitted. Waiting for network confirmation.`,
      [DepositSubStepStatus.Error]:
        'The wallet request was declined. Nothing was submitted; you can try again.',
    },
  },
  'batch-deposit': {
    title: (ctx) => `Deposit to ${ctx.trancheLabel}`,
    primary: {
      [DepositSubStepStatus.Loading]: 'Confirming deposit',
      [DepositSubStepStatus.Error]: 'Retry deposit',
    },
    alert: {
      [DepositSubStepStatus.Ready]: 'Execute batch transaction to deposit wYLDS into Vault.',
      [DepositSubStepStatus.Loading]:
        'Batch deposit transaction was submitted. Waiting for network confirmation.',
      [DepositSubStepStatus.Error]:
        'You declined the batch deposit. Nothing was submitted; you can try again.',
    },
  },
};

export const SWAP_SKIPPED_COMPLETED_LABEL = 'Using existing wYLDS';

const SWAP_NO_BATCH_STAGES: StageCopy[] = [
  {
    id: 'usdc-wylds',
    title: `USDC${STAGE_TITLE_SEPARATOR}wYLDS`,
    completedLabel: 'Swap confirmed',
    stepIds: ['approve-swap', 'swap-usdc'],
  },
  {
    id: 'wylds-mzusd',
    title: `wYLDS${STAGE_TITLE_SEPARATOR}mzUSD`,
    completedLabel: 'Mint confirmed',
    stepIds: ['approve-wylds', 'mint-mzusd'],
  },
  {
    id: 'mzusd-tranche',
    title: (ctx) => `mzUSD${STAGE_TITLE_SEPARATOR}${ctx.trancheLabel}`,
    completedLabel: 'Deposit confirmed',
    stepIds: ['approve-mzusd', 'deposit-tranche'],
  },
];

const SWAP_BATCH_STAGES: StageCopy[] = [
  {
    id: 'usdc-wylds',
    title: `USDC${STAGE_TITLE_SEPARATOR}wYLDS`,
    completedLabel: 'Swap confirmed',
    stepIds: ['approve-swap', 'swap-usdc'],
  },
  {
    id: 'wylds-tranche',
    title: (ctx) => `wYLDS${STAGE_TITLE_SEPARATOR}${ctx.trancheLabel}`,
    completedLabel: 'Deposit confirmed',
    stepIds: ['approve-wylds', 'mint-mzusd', 'approve-mzusd', 'deposit-tranche'],
    uiStepId: 'batch-deposit',
  },
];

export const STAGES_BY_FLOW: Record<DepositStepperFlow, StageCopy[]> = {
  [DepositStepperFlow.SwapBatch]: SWAP_BATCH_STAGES,
  [DepositStepperFlow.SwapNoBatch]: SWAP_NO_BATCH_STAGES,
};

export const resolveDepositStepperCopy = (text: CopyText, ctx: DepositStepperCopyCtx): string =>
  typeof text === 'function' ? text(ctx) : text;
